import { z } from 'zod';
import { ROLE_CODES, USER_STATUSES } from '../enums';
import { concurrency, listQuery, text } from './common';

export const PASSWORD_MIN_LENGTH = 10;

export const password = z
  .string({ error: 'Password is required' })
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(128, 'Password must be at most 128 characters');

export const email = z
  .string({ error: 'Email is required' })
  .trim()
  .toLowerCase()
  .pipe(z.email('Enter a valid email address').max(191));

export const loginSchema = z.object({
  email,
  password: z.string().min(1, 'Password is required').max(128),
});
export type LoginInput = z.output<typeof loginSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required').max(128),
    newPassword: password,
  })
  .refine((v) => v.currentPassword !== v.newPassword, {
    path: ['newPassword'],
    message: 'New password must be different from the current password',
  });

export const userCreateSchema = z.object({
  email,
  name: text(120, 'Name'),
  roleCode: z.enum(ROLE_CODES),
  password,
});
export type UserCreateInput = z.output<typeof userCreateSchema>;

export const userUpdateSchema = z
  .object({
    name: text(120, 'Name'),
    roleCode: z.enum(ROLE_CODES),
    status: z.enum(USER_STATUSES),
  })
  .partial()
  .extend(concurrency);
export type UserUpdateInput = z.output<typeof userUpdateSchema>;

export const userResetPasswordSchema = z.object({ password });

export const userListSchema = listQuery.extend({
  status: z.enum(USER_STATUSES).optional(),
  roleCode: z.enum(ROLE_CODES).optional(),
});

// ── App lock (PIN / passkey) ─────────────────────────────────────────

export const PIN_RE = /^\d{4,6}$/;
export const pin = z.string().regex(PIN_RE, 'PIN must be 4 to 6 digits');

/** Set or change the app-lock PIN. Changing an existing PIN needs the password. */
export const setPinSchema = z.object({
  pin,
  currentPassword: z.string().max(128).optional(),
});
export type SetPinInput = z.output<typeof setPinSchema>;

/** Unlock a locked session with the PIN, or with the password as a fallback. */
export const unlockSchema = z
  .object({ pin: pin.optional(), password: z.string().min(1).max(128).optional() })
  .refine((v) => !!v.pin !== !!v.password, { message: 'Enter your PIN or your password' });
export type UnlockInput = z.output<typeof unlockSchema>;

/** A WebAuthn response from the browser (checked in full by the server library). */
const credentialJson = z.looseObject({
  id: z.string().min(1).max(1400),
  rawId: z.string().min(1).max(1400),
  type: z.literal('public-key'),
  response: z.looseObject({}),
});
export const passkeyRegisterSchema = z.object({
  response: credentialJson,
  deviceName: z.string().trim().min(1, 'Name this device').max(100),
});
export const passkeyAuthSchema = z.object({
  response: credentialJson,
  /** From the login options call (sign-in only). */
  challengeId: z.string().max(64).optional(),
});
